import { useState, useEffect, useCallback, useRef } from 'react'
import { flushSync } from 'react-dom'
import axios from 'axios'
import { apiClient, ApiErrorResponse, handleApiRequest } from '@/services/api/client'
import { DeleteProgressDialog } from '@/components/common/DeleteProgressDialog'
import { extractErrorMessage } from '@/utils/errorHandler'
import { formatFileSize } from '@/utils/fileUtils'
import { CheckSquare, FileText, Square, Trash2, Upload } from 'lucide-react'

interface UploadedFile {
  id: string
  original_filename: string
  file_type: string
  file_category: string
  file_size: number
  uploaded_at: string
  mime_type?: string
  is_deleted?: boolean
  deleted_at?: string | null
}

interface BulkDeleteResponse {
  requested_count: number
  deleted_count: number
  not_found_count: number
  deleted_ids: string[]
  permanent: boolean
}

const TYPE_ICONS: Record<string, string> = {
  'pdf': '📄', 'xlsx': '📊', 'xls': '📊', 'csv': '📋',
  'png': '🖼️', 'jpg': '🖼️', 'jpeg': '🖼️', 'html': '✉️',
}

const CATEGORY_LABELS: Record<string, string> = {
  'timetable': 'Stundenplan',
  'teacher_list': 'Lehrerliste',
  'holiday_plan': 'Feiertagsplan',
  'qualification_matrix': 'Qualifikationsmatrix',
  'reference': 'Referenz',
  'user_import': 'Benutzer-Import',
  'timetable_fallback': 'Fallback (intern)',
  'prep_attachment': 'Prep-Anhang (intern)',
  'substitution_attachment': 'Vertretungs-Anhang',
  'email': 'E-Mails',
  'other': 'Sonstiges',
}

type DeleteMode = 'soft' | 'hard'
type ConfirmDeleteTarget = { id: string; name: string; mode: DeleteMode } | { type: 'bulk'; mode: DeleteMode }

const BULK_CHUNK_SIZE = 500
const HARD_CHUNK_SIZE = 25
const HARD_CHUNK_TIMEOUT_MS = 120_000
const CHUNK_RETRIES = 2

function isRetryableDeleteError(err: unknown): boolean {
  if (axios.isCancel(err)) return false
  if (err instanceof ApiErrorResponse) {
    return err.statusCode >= 500 || err.response === undefined
  }
  return true
}

export function FileManagementPage() {
  const [files, setFiles] = useState<UploadedFile[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [selectedFileIds, setSelectedFileIds] = useState<Set<string>>(new Set())
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [deletingBulk, setDeletingBulk] = useState(false)
  const [deleteProgress, setDeleteProgress] = useState({ current: 0, total: 0, retrying: false })
  const [confirmDelete, setConfirmDelete] = useState<ConfirmDeleteTarget | null>(null)
  const [categoryFilter, setCategoryFilter] = useState<string>('')
  const [showDeleted, setShowDeleted] = useState(false)
  const [restoringId, setRestoringId] = useState<string | null>(null)
  const [preview, setPreview] = useState<{ name: string; url: string } | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)

  const fetchFiles = useCallback(async (): Promise<UploadedFile[] | null> => {
    setLoading(true)
    try {
      const allFiles: UploadedFile[] = []
      let page = 1
      const pageSize = 100
      for (;;) {
        const params: Record<string, unknown> = { page, page_size: pageSize }
        if (categoryFilter) params.file_category = categoryFilter
        if (showDeleted) params.include_deleted = true
        const res = await handleApiRequest<{ files: UploadedFile[], total: number }>(
          apiClient.get('/api/v1/files', { params })
        )
        allFiles.push(...res.files)
        if (allFiles.length >= res.total || res.files.length === 0) break
        page += 1
      }
      setFiles(allFiles)
      setSelectedFileIds(prev => {
        const validIds = new Set(allFiles.map(f => f.id))
        return new Set([...prev].filter(id => validIds.has(id)))
      })
      setError(null)
      return allFiles
    } catch (err: unknown) {
      const errorMessage = extractErrorMessage(err, {
        loadError: 'Dateien konnten nicht geladen werden',
      })
      console.error('Failed to load files:', err)
      setError(errorMessage)
      return null
    } finally {
      setLoading(false)
    }
  }, [categoryFilter, showDeleted])

  useEffect(() => { fetchFiles() }, [fetchFiles])

  useEffect(() => {
    if (!success && !error) return
    const timer = setTimeout(() => {
      setSuccess(null)
      setError(null)
    }, 6000)
    return () => clearTimeout(timer)
  }, [success, error])

  useEffect(() => {
    if (!confirmDelete) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setConfirmDelete(null)
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [confirmDelete])

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  // Clear selection when filter changes to avoid confusion
  useEffect(() => {
    setSelectedFileIds(new Set())
  }, [categoryFilter, showDeleted])

  const mimeFor = (f: UploadedFile) => {
    if (f.mime_type) return f.mime_type.split(';')[0]
    if (f.file_type === 'html') return 'text/html'
    if (f.file_type === 'pdf') return 'application/pdf'
    if (f.file_type === 'csv') return 'text/csv'
    return 'application/octet-stream'
  }

  const fetchFileBlob = async (id: string) => {
    const res = await apiClient.get(`/api/v1/files/${id}/download`, { responseType: 'blob' })
    return res.data as Blob
  }

  const handleDownload = async (f: UploadedFile) => {
    try {
      const blob = await fetchFileBlob(f.id)
      const url = URL.createObjectURL(new Blob([blob], { type: mimeFor(f) }))
      const a = document.createElement('a')
      a.href = url
      a.download = f.original_filename
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      setError('Datei konnte nicht heruntergeladen werden')
    }
  }

  const closePreview = () => {
    setPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev.url)
      return null
    })
  }

  const handleOpen = async (f: UploadedFile) => {
    try {
      const blob = await fetchFileBlob(f.id)
      const url = URL.createObjectURL(new Blob([blob], { type: mimeFor(f) }))
      setPreview((prev) => {
        if (prev) URL.revokeObjectURL(prev.url)
        return { name: f.original_filename, url }
      })
    } catch {
      setError('Datei konnte nicht geöffnet werden')
    }
  }

  const handleRestore = async (id: string, name: string) => {
    setRestoringId(id)
    try {
      await handleApiRequest(apiClient.post(`/api/v1/files/${id}/restore`))
      const refreshed = await fetchFiles()
      setSuccess(refreshed ? `"${name}" wiederhergestellt` : `"${name}" wiederhergestellt (Liste konnte nicht aktualisiert werden)`)
    } catch {
      setError('Konnte Datei nicht wiederherstellen')
    } finally {
      setRestoringId(null)
    }
  }

  const handleDelete = async (id: string, name: string, permanent: boolean = false) => {
    setDeletingId(id)
    const modeText = permanent ? ' endgültig' : ''
    try {
      const endpoint = permanent ? `/api/v1/files/${id}/permanent` : `/api/v1/files/${id}`
      await handleApiRequest(
        permanent
          ? apiClient.delete(endpoint, { timeout: HARD_CHUNK_TIMEOUT_MS })
          : apiClient.delete(endpoint),
      )
      setFiles(prev => prev.filter(f => f.id !== id))
      setSelectedFileIds(prev => {
        const next = new Set(prev)
        next.delete(id)
        return next
      })
      const refreshed = await fetchFiles()
      setSuccess(refreshed ? `"${name}"${modeText} gelöscht` : `"${name}"${modeText} gelöscht (Liste konnte nicht aktualisiert werden)`)
    } catch {
      if (permanent) {
        const list = await fetchFiles()
        if (list && !list.some(f => f.id === id)) {
          setSelectedFileIds(prev => {
            const next = new Set(prev)
            next.delete(id)
            return next
          })
          setSuccess(`"${name}"${modeText} gelöscht`)
          return
        }
      }
      setError('Konnte Datei nicht löschen')
    } finally {
      setDeletingId(null)
    }
  }

  const handleBulkDelete = async (permanent: boolean = false) => {
    const ids = Array.from(selectedFileIds)
    if (ids.length === 0) return

    const controller = new AbortController()
    abortControllerRef.current = controller
    const { signal } = controller
    const chunkSize = permanent ? HARD_CHUNK_SIZE : BULK_CHUNK_SIZE
    const timeout = permanent ? HARD_CHUNK_TIMEOUT_MS : undefined

    setDeletingBulk(true)
    setDeleteProgress({ current: 0, total: ids.length, retrying: false })

    const succeeded: string[] = []
    let totalDeleted = 0
    let totalNotFound = 0
    let failedChunks = 0

    for (let i = 0; i < ids.length; i += chunkSize) {
      if (signal.aborted) break
      const chunk = ids.slice(i, i + chunkSize)
      for (let attempt = 0; attempt <= CHUNK_RETRIES; attempt++) {
        if (signal.aborted) break
        if (attempt > 0) {
          flushSync(() => {
            setDeleteProgress({ current: succeeded.length, total: ids.length, retrying: true })
          })
        }
        try {
          const res = await handleApiRequest<BulkDeleteResponse>(
            apiClient.post(
              '/api/v1/files/bulk-delete',
              { file_ids: chunk, permanent },
              { signal, timeout },
            ),
          )
          const deletedIds = res.deleted_ids.map(String)
          const doneIds = permanent ? chunk : deletedIds
          for (const id of doneIds) succeeded.push(id)
          totalDeleted += res.deleted_count
          totalNotFound += res.not_found_count
          setFiles(prev => prev.filter(f => !doneIds.includes(f.id)))
          break
        } catch (err: unknown) {
          if (axios.isCancel(err) || signal.aborted) break
          if (attempt < CHUNK_RETRIES && isRetryableDeleteError(err)) continue
          failedChunks += 1
          break
        }
      }
      flushSync(() => {
        setDeleteProgress({
          current: succeeded.length,
          total: ids.length,
          retrying: false,
        })
      })
    }

    const wasCancelled = signal.aborted

    setSelectedFileIds(prev => {
      const next = new Set(prev)
      for (const id of succeeded) next.delete(id)
      return next
    })

    const refreshed = await fetchFiles()
    const modeText = permanent ? ' endgültig' : ''

    if (wasCancelled) {
      setSuccess(
        succeeded.length === 0
          ? 'Löschvorgang abgebrochen. Keine Dateien gelöscht.'
          : `Löschvorgang abgebrochen. ${succeeded.length} von ${ids.length} Dateien${modeText} gelöscht.`,
      )
    } else if (failedChunks > 0) {
      setError(
        succeeded.length === 0
          ? 'Löschvorgang fehlgeschlagen'
          : `${succeeded.length} von ${ids.length} Dateien${modeText} gelöscht (${failedChunks} Batch fehlgeschlagen)`,
      )
    } else if (succeeded.length === 0) {
      setError('Keine Dateien konnten gelöscht werden')
    } else if (!permanent && totalNotFound > 0) {
      setSuccess(
        refreshed
          ? `${totalDeleted} von ${ids.length} Dateien${modeText} gelöscht (${totalNotFound} nicht gefunden)`
          : `${totalDeleted} von ${ids.length} Dateien${modeText} gelöscht (Liste konnte nicht aktualisiert werden)`,
      )
    } else {
      setSuccess(
        refreshed
          ? `${succeeded.length} Dateien${modeText} gelöscht`
          : `${succeeded.length} Dateien${modeText} gelöscht (Liste konnte nicht aktualisiert werden)`,
      )
    }

    setDeleteProgress({ current: 0, total: 0, retrying: false })
    setDeletingBulk(false)
    abortControllerRef.current = null
  }

  const handleCancelDelete = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
  }

  const confirmAndDelete = () => {
    if (!confirmDelete) return
    const permanent = confirmDelete.mode === 'hard'
    if ('type' in confirmDelete) {
      handleBulkDelete(permanent)
    } else {
      handleDelete(confirmDelete.id, confirmDelete.name, permanent)
    }
    setConfirmDelete(null)
  }

  const toggleSelect = (id: string) => {
    if (deletingBulk || deletingId !== null) return
    setSelectedFileIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleSelectAll = () => {
    if (files.length > 0 && selectedFileIds.size === files.length) {
      setSelectedFileIds(new Set())
    } else {
      setSelectedFileIds(new Set(files.map(f => f.id)))
    }
  }

  const clearSelection = () => setSelectedFileIds(new Set())

  const allSelected = files.length > 0 && selectedFileIds.size === files.length

  return (
    <div className="flex flex-col min-h-full">
      <div className="px-4 lg:px-6 py-3 border-b bg-white flex items-center gap-3 flex-shrink-0">
        <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center">
          <FileText className="w-5 h-5 text-indigo-600" />
        </div>
        <div>
          <h1 className="text-lg font-bold">Dateiverwaltung</h1>
          <p className="text-xs text-gray-500 max-w-2xl">
            Verwalten Sie hochgeladene Dateien für Stundenplan-Import, PDF-Extraktion und Template-Training.
          </p>
        </div>
      </div>

      <div className="px-4 lg:px-6 py-3">
        <p className="text-xs text-gray-500">
          Hochgeladene Dateien werden für den Stundenplan-Import und die PDF-Extraktion verwendet.
          Soft-Delete markiert Dateien als gelöscht (können wiederhergestellt werden), Hard-Delete entfernt sie permanent.
          E-Mails und interne Dateien (Fallback, Prep-Anhänge) werden standardmäßig ausgeblendet, können aber über den Filter angezeigt werden.
        </p>
      </div>

      <div className="px-4 lg:px-6 pb-6 flex-1">
        <div className="mb-4 flex items-center gap-3">
          <label htmlFor="category-filter" className="text-sm text-gray-600">Kategorie:</label>
          <select
            id="category-filter"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="">Alle (ohne E-Mails/interne)</option>
            <option value="timetable">Stundenplan</option>
            <option value="teacher_list">Lehrerliste</option>
            <option value="holiday_plan">Feiertagsplan</option>
            <option value="qualification_matrix">Qualifikationsmatrix</option>
            <option value="reference">Referenz</option>
            <option value="user_import">Benutzer-Import</option>
            <option value="email">E-Mails</option>
            <option value="other">Sonstiges</option>
            <option value="timetable_fallback">Fallback (intern)</option>
            <option value="prep_attachment">Prep-Anhang (intern)</option>
          </select>
          <label className="flex items-center gap-1.5 text-sm text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={showDeleted}
              onChange={(e) => setShowDeleted(e.target.checked)}
              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            Gelöschte anzeigen
          </label>
        </div>

        {success && <div className="mb-4 p-3 bg-green-100 text-green-700 rounded">{success}</div>}
        {error && <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">{error}</div>}

        {selectedFileIds.size > 0 && (
          <div className="mb-4 flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <span className="text-sm font-medium text-blue-700">
              {selectedFileIds.size} {selectedFileIds.size === 1 ? 'Datei' : 'Dateien'} ausgewählt
            </span>
            <button
              onClick={() => setConfirmDelete({ type: 'bulk', mode: 'soft' })}
              disabled={deletingBulk || deletingId !== null}
              className="px-3 py-1.5 text-sm bg-orange-600 text-white rounded hover:bg-orange-700 flex items-center gap-1.5 disabled:opacity-50"
            >
              <Trash2 className="w-3.5 h-3.5" />
              {deletingBulk ? 'Löschen...' : 'Soft Delete'}
            </button>
            <button
              onClick={() => setConfirmDelete({ type: 'bulk', mode: 'hard' })}
              disabled={deletingBulk || deletingId !== null}
              className="px-3 py-1.5 text-sm bg-red-600 text-white rounded hover:bg-red-700 flex items-center gap-1.5 disabled:opacity-50"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Hard Delete
            </button>
            <button
              onClick={clearSelection}
              disabled={deletingBulk}
              className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 ml-auto disabled:opacity-50"
            >
              Abbrechen
            </button>
          </div>
        )}

        {loading ? (
          <div className="bg-white rounded-lg border p-8 text-center text-gray-500">
            Dateien laden...
          </div>
        ) : files.length === 0 ? (
          <div className="bg-white rounded-lg border p-8 text-center">
            <div className="flex flex-col items-center gap-2">
              <Upload className="w-8 h-8 text-gray-300" />
              {categoryFilter === 'email' ? (
                <>
                  <p className="text-gray-500">Keine E-Mails</p>
                  <p className="text-xs text-gray-400">
                    Versendete E-Mails erscheinen hier im Live- und Datei-Modus.
                  </p>
                </>
              ) : (
                <>
                  <p className="text-gray-500">Noch keine Dateien hochgeladen</p>
                  <p className="text-xs text-gray-400">
                    Laden Sie Stundenplan-Dateien (PDF, XLSX, CSV) über die Import-Seite hoch.
                  </p>
                </>
              )}
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg border overflow-x-auto">
            <table className="w-full" aria-label="Hochgeladene Dateien">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 w-10">
                    <button
                      onClick={toggleSelectAll}
                      disabled={deletingBulk || deletingId !== null}
                      aria-label={allSelected ? 'Auswahl aufheben' : 'Alle auswählen'}
                      className="p-0.5 hover:bg-gray-200 rounded disabled:opacity-50"
                    >
                      {allSelected ? (
                        <CheckSquare className="w-4 h-4 text-blue-600" />
                      ) : (
                        <Square className="w-4 h-4 text-gray-400" />
                      )}
                    </button>
                  </th>
                  {['Datei', 'Kategorie', 'Typ', 'Grosse', 'Hochgeladen', 'Aktionen'].map(h => (
                    <th key={h} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {files.map(f => {
                  const isSelected = selectedFileIds.has(f.id)
                  return (
                    <tr key={f.id} className={`hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''} ${f.is_deleted ? 'opacity-60' : ''}`}>
                      <td className="px-4 py-2">
                        <button
                          onClick={() => toggleSelect(f.id)}
                          disabled={deletingBulk || deletingId !== null}
                          aria-label={isSelected ? 'Auswahl aufheben' : 'Auswählen'}
                          className="p-0.5 hover:bg-gray-200 rounded disabled:opacity-50"
                        >
                          {isSelected ? (
                            <CheckSquare className="w-4 h-4 text-blue-600" />
                          ) : (
                            <Square className="w-4 h-4 text-gray-400" />
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-2 text-sm">
                        <div className="flex items-center gap-2">
                          <span>{TYPE_ICONS[f.file_type] || '📁'}</span>
                          <span className="font-medium truncate max-w-[200px]" title={f.original_filename}>
                            {f.original_filename}
                          </span>
                          {f.is_deleted && (
                            <span
                              className="text-[10px] font-medium text-gray-500 bg-gray-100 border border-gray-200 rounded px-1.5 py-0.5 flex-shrink-0"
                              title={f.deleted_at ? `Gelöscht am ${new Date(f.deleted_at).toLocaleString('de-CH')}` : 'Gelöscht'}
                            >
                              {f.deleted_at ? `Gelöscht am ${new Date(f.deleted_at).toLocaleDateString('de-CH')}` : 'Gelöscht'}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-600">
                        {CATEGORY_LABELS[f.file_category] || f.file_category}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-600">{f.file_type}</td>
                      <td className="px-4 py-2 text-sm text-gray-600">{formatFileSize(f.file_size)}</td>
                      <td className="px-4 py-2 text-sm text-gray-600">
                        {new Date(f.uploaded_at).toLocaleString('de-CH')}
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex gap-1">
                          {f.is_deleted ? (
                            <>
                              <button
                                onClick={() => handleRestore(f.id, f.original_filename)}
                                disabled={restoringId === f.id || deletingId !== null || deletingBulk}
                                aria-label={`${f.original_filename} wiederherstellen`}
                                className="text-xs text-green-600 hover:text-green-800 px-2 py-1 border border-green-200 rounded hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {restoringId === f.id ? 'Wiederherstellen...' : 'Wiederherstellen'}
                              </button>
                              <button
                                onClick={() => setConfirmDelete({ id: f.id, name: f.original_filename, mode: 'hard' })}
                                disabled={deletingId === f.id || restoringId !== null || deletingBulk}
                                aria-label={`${f.original_filename} endgültig löschen`}
                                className="text-xs text-red-600 hover:text-red-800 px-2 py-1 border border-red-200 rounded hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {deletingId === f.id ? 'Löschen...' : 'Endgültig löschen'}
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => handleOpen(f)}
                                disabled={deletingId !== null || deletingBulk}
                                aria-label={`${f.original_filename} öffnen`}
                                className="text-xs text-indigo-600 hover:text-indigo-800 px-2 py-1 border border-indigo-200 rounded hover:bg-indigo-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                Öffnen
                              </button>
                              <button
                                onClick={() => handleDownload(f)}
                                disabled={deletingId !== null || deletingBulk}
                                aria-label={`${f.original_filename} herunterladen`}
                                className="text-xs text-gray-700 hover:text-gray-900 px-2 py-1 border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                Download
                              </button>
                              <button
                                onClick={() => setConfirmDelete({ id: f.id, name: f.original_filename, mode: 'soft' })}
                                disabled={deletingId === f.id || deletingBulk}
                                aria-label={`${f.original_filename} soft löschen`}
                                className="text-xs text-orange-600 hover:text-orange-800 px-2 py-1 border border-orange-200 rounded hover:bg-orange-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {deletingId === f.id ? 'Löschen...' : 'Soft Delete'}
                              </button>
                              <button
                                onClick={() => setConfirmDelete({ id: f.id, name: f.original_filename, mode: 'hard' })}
                                disabled={deletingId === f.id || deletingBulk}
                                aria-label={`${f.original_filename} endgültig löschen`}
                                className="text-xs text-red-600 hover:text-red-800 px-2 py-1 border border-red-200 rounded hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                Hard Delete
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {deletingBulk && deleteProgress.total > 0 && (
        <DeleteProgressDialog
          title="Lösche Dateien..."
          current={deleteProgress.current}
          total={deleteProgress.total}
          status={`${deleteProgress.current} gelöscht, ${Math.max(0, deleteProgress.total - deleteProgress.current)} noch offen`}
          hint={deleteProgress.retrying ? 'Batch wird wiederholt...' : undefined}
          onCancel={handleCancelDelete}
        />
      )}

      {preview && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={(e) => { if (e.target === e.currentTarget) closePreview() }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="file-preview-title"
            className="bg-white rounded-lg shadow-xl w-full max-w-5xl h-[90vh] flex flex-col"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h2 id="file-preview-title" className="text-sm font-medium truncate pr-4">{preview.name}</h2>
              <button
                onClick={closePreview}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Schliessen
              </button>
            </div>
            <iframe title={preview.name} src={preview.url} className="flex-1 w-full border-0 rounded-b-lg bg-white" />
          </div>
        </div>
      )}

      {confirmDelete && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={(e) => { if (e.target === e.currentTarget) setConfirmDelete(null) }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-dialog-title"
            className="bg-white rounded-lg p-6 w-full max-w-sm"
          >
            <h2 id="delete-dialog-title" className="text-lg font-bold mb-2">
              {confirmDelete.mode === 'hard' ? 'Dateien endgültig löschen' : 'Dateien löschen'}
            </h2>
            <p className="text-sm text-gray-600 mb-3">
              {confirmDelete.mode === 'hard'
                ? ('type' in confirmDelete
                    ? `${selectedFileIds.size} Dateien endgültig löschen?`
                    : `"${confirmDelete.name}" endgültig löschen?`)
                : ('type' in confirmDelete
                    ? `${selectedFileIds.size} Dateien wirklich löschen?`
                    : `"${confirmDelete.name}" wirklich löschen?`)}
            </p>
            <div className={`rounded-lg p-3 mb-4 border ${confirmDelete.mode === 'hard' ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}>
              <div className={`text-sm ${confirmDelete.mode === 'hard' ? 'text-red-700' : 'text-gray-600'}`}>
                {confirmDelete.mode === 'hard'
                  ? 'Endgültig gelöschte Dateien können nicht wiederhergestellt werden.'
                  : "Die Datei wird als gelöscht markiert und kann über 'Gelöschte anzeigen' wiederhergestellt werden."}
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t">
              <button
                onClick={() => setConfirmDelete(null)}
                autoFocus
                className="px-4 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50"
              >
                Abbrechen
              </button>
              <button
                onClick={confirmAndDelete}
                className="px-4 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700"
              >
                Löschen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
